import "./index.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import UploadItem from "./components/UploadItem";
import UploadSelectionModal from "./components/UploadSelectionModal";
import VideoLinkInput from "./components/VideoLinkInput";
import Firebase from "../../firebasehelper";
import PDFViewerModal from "./components/PDFViewerModal";
import { getFilePath } from "../../Utils/utils";
import {
  IMAGE_UPLOAD,
  GIF_UPLOAD,
  VIDEO_UPLOAD,
  PDF_UPLOAD,
  YOUTUBE_URL,
} from "../../Constants/types";
import Tag from "./components/Tag";
import TagDropdown from "./components/TagDropdown";

// This file aims to replace the old /AddNoteModal/UploadPanel
// component in the near future.
export default function UploadPanel({
  media,
  updateState,
  hex,
  white,
  brandId,
  mediaToDelete,
  uploadDirectory,
  uploadLimit = 8,
  shouldResetYoutubeInput = false,
  isEditModal = false,
  onTagChange = null,
  tagOptions = null,
}) {
  const modalRef = useRef();
  const fileInputRef = useRef();
  const dropdownRefs = useRef([]);
  const buttonRefs = useRef([]);

  const [openDropdownId, setOpenDropdownId] = useState(null);

  const [isOpen, setOpen] = useState(false);
  const [isUrlnputOpen, SetUrlInputOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const onOutsideClick = (e) => {
      if (isOpen && !modalRef.current.contains(e.target)) {
        setSelectedFileType(null);
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, []);

  useEffect(() => {
    const onOutsideTagDropdownClick = (e) => {
      const { current: dropdowns } = dropdownRefs;
      const { current: buttons } = buttonRefs;

      if (openDropdownId !== null) {
        const openDropdownRef = dropdowns[openDropdownId];
        const buttonRef = buttons[openDropdownId];

        const isOutsideButton = buttonRef
          ? !buttonRef.contains(e.target)
          : true;
        const isOutsideDropdown = openDropdownRef
          ? !openDropdownRef.contains(e.target)
          : true;

        if (isOutsideDropdown && isOutsideButton) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener("mousedown", onOutsideTagDropdownClick);
    return () =>
      document.removeEventListener("mousedown", onOutsideTagDropdownClick);
  }, [openDropdownId]);

  useEffect(() => {
    const openFileBrowser = () => fileInputRef.current.click();

    if (selectedFileType && selectedFileType.type === YOUTUBE_URL) {
      SetUrlInputOpen(true);
    } else if (selectedFileType) {
      openFileBrowser();
    }
  }, [selectedFileType]);

  useEffect(() => {
    const resetVideoInputComponent = () => {
      SetUrlInputOpen(false);
      updateState({ shouldResetYoutubeInput: false });
    };

    if (shouldResetYoutubeInput) {
      resetVideoInputComponent();
    }
  }, [shouldResetYoutubeInput]);

  const onPdfClick = (pdfURL) => setSelectedPDF(pdfURL);

  const onOpen = () => {
    SetUrlInputOpen(false);
    setOpen(true);
  };

  const onSelectFile = (fileType) => {
    setSelectedFileType(fileType);
    setOpen(false);
  };

  const uploadVideo = async (file, folder) => {
    return new Promise((resolve, reject) => {
      const uploadTask = Firebase.uploadVideo(file, folder);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.floor(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
        },
        (e) => reject(e),
        async () => {
          try {
            const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const onFileUpload = async (e) => {
    if (!Object.keys(e.target.files).length) return;
    setLoading(true);

    const { type } = selectedFileType;
    const title = new Date().getTime().toString();
    let assetUrl = null;

    try {
      if (type === IMAGE_UPLOAD || type === GIF_UPLOAD) {
        const path = `${uploadDirectory}/${brandId}/${
          type === IMAGE_UPLOAD ? "images" : "gifs"
        }`;
        assetUrl = await Firebase.uploadFile(e, title, path);
      } else if (type === VIDEO_UPLOAD) {
        const path = `${uploadDirectory}/${brandId}/videos`;
        assetUrl = await uploadVideo(e.target.files[0], path);
      } else if (type === PDF_UPLOAD) {
        const path = `${uploadDirectory}/${brandId}/pdfs`;
        assetUrl = await Firebase.uploadPDF(e.target.files[0], title, path);
      }

      if (assetUrl) {
        const newFile = { mediaUrl: assetUrl, mediaType: type };
        const existingMedia = media.filter((upload) => upload);
        const updatedMedia = [...existingMedia, newFile];
        updateState({ media: updatedMedia });
      }

      resets();
    } catch (e) {
      console.error("fileUpload", e);
      resets();
    }
  };

  const resets = () => {
    setSelectedFileType(null);
    setLoading(false);
    selectedFileType.type === VIDEO_UPLOAD && setUploadProgress(0);
    // reset file input to allow users to upload the same file again
    fileInputRef.current.value = "";
  };

  const onClose = () => {
    setSelectedFileType(null);
    setOpen(false);
  };

  const onAddVideoLink = (assetUrl) => {
    const newFile = { mediaUrl: assetUrl, mediaType: YOUTUBE_URL };
    const existingMedia = media.filter((upload) => upload);

    const updatedMedia = [...existingMedia, newFile];
    updateState({ media: updatedMedia });
    SetUrlInputOpen(false);
  };

  const deleteFile = async (mediaObj) => {
    setLoading(true);
    const { mediaType, mediaUrl } = mediaObj;

    if (mediaType !== YOUTUBE_URL) {
      const path = getFilePath(uploadDirectory, mediaUrl, mediaType);
      await Firebase.deleteFile(path);
    }

    const updatedMedia = media.filter(
      (item) => item && item.mediaUrl !== mediaUrl
    );
    updateState({ media: updatedMedia });
    setLoading(false);
  };

  const queueForDeletion = (selectedMedia) => {
    const updatedMediaToDelete = [...mediaToDelete, selectedMedia];
    const updatedMedia = media.filter(
      (item) => item.mediaUrl !== selectedMedia.mediaUrl
    );

    updateState({
      media: updatedMedia,
      mediaToDelete: updatedMediaToDelete,
    });
  };

  const getDeleteFunction = () => {
    // in add product modal files can always be deleted
    if (!isEditModal) return deleteFile;

    if (media && media.length === 1) return null;
    if (isEditModal) return queueForDeletion;
    return deleteFile;
  };

  const addPlaceholders = (media, uploadLimit) => {
    let updatedMedia = [];
    if (!media || media.length < uploadLimit) {
      updatedMedia = media ? [...media] : [];
      const numFreeSlots = uploadLimit - (media ? media.length : 0) - 1;

      for (let i = 0; i < numFreeSlots; i++) {
        updatedMedia.push(null);
      }
      return updatedMedia;
    }
    return media;
  };

  const inputType = useMemo(() => {
    return selectedFileType && selectedFileType.type === YOUTUBE_URL
      ? "url"
      : "file";
  }, [selectedFileType]);

  const onSelectTag = (idx, option) => {
    onTagChange(idx, option);
    setOpenDropdownId(null);
  };

  const toggleDropdown = (index) => {
    setOpenDropdownId((prevId) => (prevId === index ? null : index));
  };

  const getAvailableTags = (uploads, selectedOption) => {
    const tag = selectedOption ? selectedOption.tag : "";

    const availableTags = tagOptions.reduce((acc, cur) => {
      const isInUse = uploads.some(
        (upload) => upload.tag && upload.tag === cur.value && upload.tag !== tag
      );

      if (!isInUse) {
        acc.push(cur);
      }
      return acc;
    }, []);
    return [{ label: "No tag", value: "" }, ...availableTags];
  };

  const mediaArray = useMemo(() => {
    return addPlaceholders(media, uploadLimit);
  }, [media, uploadLimit]);

  return (
    <div className="upload-panel">
      {mediaArray.length < uploadLimit && (
        <button
          className="upload-panel__upload-button"
          style={{ "--hex": hex }}
          title="Upload content"
          onClick={onOpen}
        >
          +
        </button>
      )}
      {mediaArray.map((item, i) => (
        <li key={i} className="upload-panel__item">
          <UploadItem
            media={item}
            onPdfClick={onPdfClick}
            onDelete={getDeleteFunction()}
          />
          <Tag
            shouldRender={tagOptions && item && item.mediaUrl}
            tag={item ? item.tag : ""}
            toggleDropdown={() => toggleDropdown(i)}
            ref={(el) => (buttonRefs.current[i] = el)}
          />
          <TagDropdown
            isOpen={tagOptions && i === openDropdownId}
            tag={item ? item.tag : ""}
            tagOptions={tagOptions ? getAvailableTags(media, item) : null}
            onTagChange={(value) => onSelectTag(i, value)}
            ref={(el) => (dropdownRefs.current[i] = el)}
          />
        </li>
      ))}
      {isLoading && (
        <div
          className={`upload-panel__loading-container ${
            uploadProgress
              ? "upload-panel__loading-container-with-progress"
              : ""
          }`}
        >
          <i className="fa fa-1x fa-sync fa-spin text-muted" />
          {uploadProgress ? (
            <span className="upload-panel__progress">
              {`${uploadProgress}%`}
            </span>
          ) : null}
        </div>
      )}
      <UploadSelectionModal
        isOpen={isOpen}
        onClose={onClose}
        hex={hex}
        modalRef={modalRef}
        onSelect={onSelectFile}
      />
      {isUrlnputOpen && (
        <VideoLinkInput
          onAddVideoLink={onAddVideoLink}
          hex={hex}
          white={white}
        />
      )}
      <input
        type={inputType}
        onChange={onFileUpload}
        ref={fileInputRef}
        style={{ display: "none" }}
        accept={selectedFileType && selectedFileType.accept}
      />
      {selectedPDF && (
        <PDFViewerModal
          pdfUrl={selectedPDF}
          setSelectedPDF={setSelectedPDF}
          hex={hex}
          white={white}
        />
      )}
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/components/UploadPanel/index.js